import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface WizardStep {
  title: string
  /** What was picked in this step, shown under the title once it is done. */
  summary?: string
}

export interface WizardStepsProps {
  steps: WizardStep[]
  /** Index of the step being shown. */
  current: number
  /** Makes finished steps clickable, to go back and change them. */
  onStepClick?: (index: number) => void
  className?: string
}

/** The numbered progress row at the top of a multi-step dialog. */
export function WizardSteps({ steps, current, onStepClick, className }: WizardStepsProps) {
  return (
    <ol className={cn('flex items-center gap-2 text-sm', className)}>
      {steps.map((step, i) => {
        const done = i < current
        const active = i === current
        const clickable = done && !!onStepClick
        return (
          <li key={step.title} className="flex min-w-0 items-center gap-2">
            {i > 0 && <span className="h-px w-6 shrink-0 bg-border" />}
            <button
              type="button"
              disabled={!clickable}
              onClick={() => onStepClick?.(i)}
              className={cn(
                'flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left',
                clickable && 'cursor-pointer hover:bg-accent'
              )}
            >
              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border text-xs',
                  active && 'border-primary bg-primary text-primary-foreground',
                  done && 'border-primary text-primary'
                )}
              >
                {done ? <Check className="size-3" /> : i + 1}
              </span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span className={cn('truncate', !active && 'text-muted-foreground')}>
                  {step.title}
                </span>
                {done && step.summary && (
                  <span className="truncate text-xs font-medium">{step.summary}</span>
                )}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}
